import { Circle, Fill, Stroke, Style, Text } from 'ol/style';
import { DEVICE_PIXEL_RATIO } from 'ol/has';

const getArealbrukFill = (at, alpha) => {
  // const at = formål2gk(code).arealtype;
  let colour = 'transparent'; // Skog will be transparent, for instance.
  if (at === 'Annen utmark' ) colour = [190, 178, 151, alpha];
  else if (at === 'Beite - Ekstensivt') colour = [214, 231, 174, alpha];
  else if (at === 'Beite - Intensivt') colour = [255, 255, 173, alpha];
  else if (at?.indexOf('Dyrket mark') === 0) colour = [255, 209, 110, alpha];
  else if (at?.indexOf('Myr') === 0) colour = [209, 209, 255, alpha];
  else if (at === 'Vann') colour = [89, 183, 238, alpha];
  return new Fill({ color: colour });
};

const getTreslagFill = (ts, alpha) => {
  // const ts = formål2gk(code).treslag;
  let colour = 'transparent';
  switch (ts) {
    case 'Furuskog':
      colour = [102, 194, 164, alpha];
      break;
    case 'Granskog':
      colour = [35, 139, 69, alpha];
      break;
    case 'Lauvskog':
      colour = [162, 204, 109, alpha];
      break;
    default:
      colour = 'transparent';
  }
  return new Fill({ color: colour });
};

const getSkogbonitetFill = (sb, alpha) => {
  // const sb = formål2gk(code).skogbonitet;
  let colour = 'transparent';
  switch (sb) {
    case 'Svært høy':
      colour = dots(4, alpha);
      break;
    case 'Høy':
      colour = dots(6, alpha);
      break;
    case 'Middels':
      colour = dots(8, alpha);
      break;
    case 'Lav':
      colour = dots(10, alpha);
      break;
    default:
      colour = 'transparent';
  }
  return new Fill({ color: colour });
};

const getOrganiskStroke = (mito) => {
  const organisk = (mito === 2);
  const colour = 'rgb(133, 182, 240)';
  return organisk ? new Stroke({ color: colour, width: 4 }) : dStroke;
};

const getBebygdFill = (kn1, kn2, alpha) => {
  let colour;
  switch (kn2) {
    case 'Boligområde':
    case 'Boligområde - By':
    case 'Boligområde - Spredt':
    case 'Boligområde - Tett':
    case 'Uspesifisert':
      colour = [255, 255, 51, alpha];
      break; // Bolig
    case 'Samferdselsområde - Bane':
    case 'Samferdselsområde - Flyplass':
    case 'Samferdselsområde - Havn':
    case 'Samferdselsområde - Infrastruktur':
    case 'Samferdselsområde - Terminal':
    case 'Samferdselsområde - Vei':
      colour = [153, 153, 153, alpha];
      break; // Samferdselsområde
    case 'Nærings- og institusjonsområde':
    case 'Nærings- og institusjonsområde - Masseuttak':
    case 'Nærings- og institusjonsområde - Reiseliv (Camping/Hytter)':
      colour = [223, 101, 176, alpha];
      break; // Nærings- og institusjonsområde
    case 'Fritidsboligområde':
      colour = [255, 204, 51, alpha];
      break; // Hytteområde
    case 'Idrettsområde - Bebygd':
    case 'Idrettsområde':
      colour = [102, 153, 0, alpha];
      break; // Idrettsområde
    case 'Gravlund':
    case 'Grøntområde':
    case 'Grøntområde - Felles opphold':
      colour = (kn1 === 'Uklassifisert') ? 'transparent' : [0, 204, 153, alpha];
      break; // Grøntområde
    case 'Bebygd':
      colour = [230, 185, 0, alpha];
      break; // Uklassifisert område
    case 'Infrastruktur':
    case 'Infrastruktur - Solkraft':
      colour = [123, 50, 148, alpha];
      break; // Infrastrukturområde
    default:
      colour = 'transparent';
  }
  return new Fill({ color: colour });
};

// Generate a canvasPattern with circles on transparent background:
const dots = (gap, alpha) => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  // Gradient and pattern are in canvas pixel space, therefore we adjust for the
  // renderer's pixel ratio:
  const pixelRatio = DEVICE_PIXEL_RATIO; // did not work on mac FF
  // const pixelRatio = 1;

  // Note: for seamless repeat patterns, image width and height of the
  // pattern image must be a factor of two (2, 4, 8, ..., 512).
  const side = gap * pixelRatio;
  canvas.width = side;
  canvas.height = side;
  // Transparent background:
  context.fillStyle = 'transparent';
  context.fillRect(0, 0, canvas.width, canvas.height);
  // Black circle:
  context.fillStyle = 'black';
  context.globalAlpha = alpha;
  context.beginPath();
  const centre = [side / 2, side / 2];
  const radius = .9 * pixelRatio;
  context.arc(centre[0], centre[1], radius, 0, 2 * Math.PI);
  context.fill();
  return context.createPattern(canvas, 'repeat');
};

// Defaul style (non suitable features/plans):
const dFill = new Fill({ color: 'transparent' });
const dStroke = new Stroke({ color: '#3399CC', width: 1.8 });
const dStyle = new Style({
  image: new Circle({ fill: dFill, stroke: dStroke, radius: 5 }),
  fill: dFill,
  stroke: dStroke
});

const thickerText = (text) => {
  // return new Text({ text: text });
  return new Text({
    fill: new Fill({ color: 'yellow' }),
    stroke: new Stroke({ color: 'gray', width: 3 }),
    text: text,
    font: 'normal italic 16px helvetica',
    textBaseline: 'ideographic',
    offsetY: -12
  });
};

export const highlightStyle = (h, dash) => {
  const c = (typeof h === 'string' || h instanceof String) ? h : 'red';
  return new Style({
    // fill: new Fill({ color: 'rgba(255, 0, 0, .1)' }),
    stroke: new Stroke({ color: c, lineDash: dash ?? null, width: 2 }),
    zIndex: 1
  })
};

export const planStyle = (feature) => {
  const p = feature.getProperties();
  const opacity_percentage = p.opacity ?? 100;
  const alpha = opacity_percentage / 100;
  const styleStack = [];
  if (p.checked && p.suitable) {
    const [w1, w2] = p.fremtidig?.split(' - ') || [null, null];
    const arealbruk = new Style({ fill: getArealbrukFill(p.fremtidig, alpha) });
    const treslag = new Style({ fill: getTreslagFill(w1, alpha) });
    const skogbonitet = new Style({ fill: getSkogbonitetFill(w2, alpha) });
    const organisk = new Style({ stroke: getOrganiskStroke(p.fremtidig) });
    const bebygd = new Style({ fill: getBebygdFill(p.kat_nivå1, p.kat_nivå2, alpha) });
    // const kraftlinje = getKraftlinjeStyle(p);
    const text = new Style({ text: thickerText(p.feltnavn) });
    styleStack.push(new Style({ fill: new Fill({ color: [255, 255, 0, alpha / 20] }) }));
    // styleStack.concat([arealtype, treslag, skogbonitet, grunnforhold]);
    // [arealbruk, treslag, skogbonitet, organisk, bebygd, kraftlinje, text]
    if (p.plankategori === 'Fremtidig')
      [arealbruk, treslag, skogbonitet].forEach(s => styleStack.push(s));
    [organisk, bebygd, text].forEach(s => styleStack.push(s));
  } else if (['KpOmråde', 'RpOmråde'].indexOf(p.objtype) > -1 || p.drawn) {
    const n = p.NASJONALAREALPLANID;
    const planid = p.title || (n && n.planidentifikasjon) || '';
    // styleStack.concat([dStyle, new Style({ text: thickerText(planid) })]);
    [dStyle, new Style({ text: thickerText(planid), zIndex: 1 })].forEach(
      s => styleStack.push(s)
    );
  }
  if (p.highlight) styleStack.push(highlightStyle(p.highlight));
  return styleStack;
};
